/**
 * FolderBreadcrumbNav Component
 * Enhanced breadcrumb navigation for folder paths
 */

import { forwardRef } from 'react';
import { Group, Text, ActionIcon } from '@/components/ui';
import { IconHome, IconChevronRight } from '@tabler/icons-react';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

export interface FolderBreadcrumbNavProps {
  /** Current folder path (e.g., "/documents/2024") */
  path: string;
  /** Callback when navigating to a folder */
  onNavigate: (path: string) => void;
  /** Maximum items to show before collapsing */
  maxItems?: number;
  /** Show home icon */
  showHome?: boolean;
  /** Additional className */
  className?: string;
}

/**
 * Folder breadcrumb navigation component
 *
 * @example
 * ```tsx
 * <FolderBreadcrumbNav
 *   path="/documents/2024/reports"
 *   onNavigate={(path) => navigateToFolder(path)}
 *   maxItems={4}
 *   showHome
 * />
 * ```
 */
export const FolderBreadcrumbNav = forwardRef<HTMLDivElement, FolderBreadcrumbNavProps>(
  (props, ref) => {
    const {
      path,
      onNavigate,
      showHome = true,
      className,
    } = props;

    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    // Parse path into breadcrumb items
    const pathParts = path.split('/').filter(Boolean);

    const breadcrumbItems = pathParts.map((part, index) => {
      const itemPath = '/' + pathParts.slice(0, index + 1).join('/');
      return {
        label: part,
        path: itemPath,
      };
    });

    // Add home if enabled
    const items = showHome
      ? [{ label: 'Home', path: '/' }, ...breadcrumbItems]
      : breadcrumbItems;

    return (
      <Group ref={ref} gap="xs" className={className}>
        {items.map((item, index) => (
          <Group key={item.path} gap="xs">
            {index === 0 && showHome ? (
              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => onNavigate(item.path)}
                aria-label="Navigate to home"
              >
                <IconHome size={16} />
              </ActionIcon>
            ) : (
              <>
                <IconChevronRight size={14} style={{ opacity: 0.4 }} />
                {index < items.length - 1 ? (
                  <div
                    onClick={() => onNavigate(item.path)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onNavigate(item.path);
                      }
                    }}
                  >
                    <Text
                      size="sm"
                      fw={400}
                      c="dimmed"
                      style={accessibilityStyles}
                    >
                      {item.label}
                    </Text>
                  </div>
                ) : (
                  <Text
                    size="sm"
                    fw={600}
                    style={{ userSelect: 'none', ...accessibilityStyles }}
                  >
                    {item.label}
                  </Text>
                )}
              </>
            )}
          </Group>
        ))}
      </Group>
    );
  }
);

FolderBreadcrumbNav.displayName = 'FolderBreadcrumbNav';

export default FolderBreadcrumbNav;
